import { Accordion, Button } from '@/components';
import ModalDrawer from '@/components/ui/ModalDrawer';
import { useGetProfessionalsPublic } from '@/services';
import { useGetQuestions } from '@/services/queries/questions';
import { useGetClubsB2B2C, useGetClubsB2C } from '@/services/queries/club';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { ProfessionalsCarousel } from './components/ProfessionalCarrousel';
import { Footer } from './components/Footer';
import { MediaLightbox } from './components/MediaLightbox';
import { ReclameAquiVerifiedSection } from '@/pages/public/business-landing/components/ReclameAquiVerifiedSection';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { encodeUrl, createClubesAuthRedirectState } from '@/utils';
import { Helmet } from 'react-helmet-async';
import { cn } from '@/lib/utils';
import {
	Building2,
	Stethoscope,
	User,
	CheckCircle2,
	Check,
	ArrowRight,
	Loader2,
	Maximize2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { WHATSAPP_LINK_FORM } from '@/constants/whatsapp';

const getClubBenefitLabel = (benefit: any) =>
	benefit?.service_substitute_title || benefit?.service?.service_name || '';

const sortBenefitsByPriority = (benefits?: any[]) =>
	[...(benefits ?? [])].sort(
		(a, b) => (b?.display_order ?? 0) - (a?.display_order ?? 0),
	);

const MAX_BENEFITS_IN_CARD = 4;
const MAX_CLUBS_IN_INDIVIDUAL_GRID = 3;

type IndividualStaticPlan = {
	id: string;
	name: string;
	slogan: string;
	priceDisplay: string;
	pricePrefix?: string;
	highlighted: boolean;
	benefits: string[];
};

const INDIVIDUAL_STATIC_PLANS: IndividualStaticPlan[] = [
	{
		id: 'mensal',
		name: 'Sessões mensais',
		slogan: 'Para começar no seu tempo, com 1 sessão por mês.',
		priceDisplay: '59,97',
		pricePrefix: 'A partir de',
		highlighted: false,
		benefits: [
			'1 sessão de psicólogo por mês',
			'Agendamento online em poucos cliques',
			'Troca de psicólogo gratuita',
			'Sem fidelidade',
		],
	},
	{
		id: 'quinzenal',
		name: 'Sessões quinzenais',
		slogan: 'O ritmo que a maioria dos terapeutas recomenda.',
		priceDisplay: '169,97',
		highlighted: true,
		benefits: [
			'2 sessões de psicólogo por mês',
			'Agendamento online em poucos cliques',
			'Troca de psicólogo gratuita',
			'Consulta médica e nutricionista com até 70% OFF',
			'Sem fidelidade',
		],
	},
	{
		id: 'semanal',
		name: 'Sessões semanais',
		slogan: 'Acompanhamento semanal, para quem busca evolução constante.',
		priceDisplay: '207,99',
		highlighted: false,
		benefits: [
			'4 sessões de psicólogo por mês',
			'Agendamento online em poucos cliques',
			'Troca de psicólogo gratuita',
			'Consulta médica e nutricionista com até 70% OFF',
			'Sem fidelidade',
		],
	},
];

const INDIVIDUAL_CHEAPEST_PRICE = 59.97;

const ClubPlanDetailsModal = ({
	club,
	onClose,
}: {
	club: Clubs.Club | null;
	onClose: () => void;
}) => {
	return (
		<ModalDrawer isOpen={!!club} onCancel={onClose} disabledConfirm={false}>
			{club && (
				<div className="w-full max-w-lg p-2 md:p-4">
					<span className="mb-4 block text-[10px] font-bold uppercase tracking-widest text-brand-orange">
						Detalhes do plano
					</span>
					<h3 className="text-3xl font-bold text-stone-900">
						{club.name}
					</h3>
					{club.slogan && (
						<p className="mt-2 text-base leading-relaxed text-stone-400">
							{club.slogan}
						</p>
					)}

					<div className="space-y-6">
						<p className="mt-6 text-lg leading-relaxed text-stone-600">
							{club.description ||
								'Conheça os detalhes deste plano.'}
						</p>

						<div className="rounded-3xl border border-gray-100 bg-gray-50 p-6">
							<h4 className="mb-4 text-xs font-bold uppercase tracking-widest">
								O que está incluso:
							</h4>
							<ul className="space-y-3">
								{sortBenefitsByPriority(
									Array.isArray(club.benefits)
										? club.benefits
										: [],
								).map((benefit: any) => (
									<li
										key={benefit.id}
										className="flex items-center gap-3 text-sm text-gray-500"
									>
										<Check className="h-4 w-4 text-brand-orange" />
										{getClubBenefitLabel(benefit)}
									</li>
								))}
							</ul>
						</div>
					</div>

					<button
						type="button"
						onClick={onClose}
						className="mt-10 w-full rounded-full bg-black py-5 text-xs font-bold tracking-widest text-white transition-colors hover:bg-gray-900"
					>
						ENTENDI
					</button>
				</div>
			)}
		</ModalDrawer>
	);
};

const Landing = () => {
	const navigate = useNavigate();
	const { data: profData, isLoading: isLoadingProfessional } =
		useGetProfessionalsPublic({});
	const { data: questionsData } = useGetQuestions();
	const { data: b2cClubsData, isLoading: isLoadingB2CClubs } =
		useGetClubsB2C();
	const { data: b2b2cClubsData, isLoading: isLoadingB2B2CClubs } =
		useGetClubsB2B2C();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const [selectedClub, setSelectedClub] = useState<Clubs.Club | null>(null);
	const [lightboxMedia, setLightboxMedia] = useState<{
		src: string;
		alt: string;
	} | null>(null);
	const isPatientAudience =
		location.pathname === '/' ||
		location.pathname === '/pacientes' ||
		searchParams.get('audience') === 'paciente';
	const isIndividualAudience = location.pathname === '/';

	const pickClubsForDisplay = (clubs: Clubs.Club[]) => {
		const sorted = [...clubs].sort(
			(a, b) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);

		const highlighted = sorted.find((c) => c.render_type === 'HIGHLIGHTED');
		const top = sorted.slice(0, 4);

		if (!highlighted) return top;
		if (top.some((c) => c.id === highlighted.id)) return top;

		if (top.length < 4) return [...top, highlighted];

		return [...top.slice(0, 3), highlighted];
	};

	const sortedB2CClubs = useMemo(() => {
		const clubs = Array.isArray(b2cClubsData) ? b2cClubsData : [];
		return pickClubsForDisplay(clubs);
	}, [b2cClubsData]);

	const sortedB2B2CClubs = useMemo(() => {
		const clubs = Array.isArray(b2b2cClubsData) ? b2b2cClubsData : [];
		return pickClubsForDisplay(clubs);
	}, [b2b2cClubsData]);

	const cheapestDisplayedB2CPrice = useMemo(() => {
		const prices = sortedB2CClubs
			.slice(0, MAX_CLUBS_IN_INDIVIDUAL_GRID)
			.map((club) => Number.parseFloat(String(club.price)))
			.filter((price) => Number.isFinite(price));

		if (prices.length === 0) return null;

		return Math.min(...prices);
	}, [sortedB2CClubs]);

	const handleCreateAccount = () => {
		navigate('/cadastro');
	};

	const handleScheduling = () => {
		const encodedParam = encodeUrl({
			s: 1,
			p: '',
		});

		searchParams.set('d', encodedParam);
		navigate(`/agendamento?${searchParams.toString()}`);
	};

	if (isPatientAudience) {
		return (
			<section className="flex w-full flex-col items-center bg-white text-gray-900">
				<Helmet>
					<title>Plumaa - Saúde Mental</title>
					<meta
						name="description"
						content="Encontre psicólogos e psiquiatras qualificados. Agende sua consulta online de forma segura e comece a cuidar da sua saúde mental hoje."
					/>
				</Helmet>

				<Header />

				<main className="w-full">
					<HeroSection
						isPatientAudience={isPatientAudience}
						isIndividualAudience={isIndividualAudience}
						cheapestDisplayedPrice={
							isIndividualAudience
								? INDIVIDUAL_CHEAPEST_PRICE
								: cheapestDisplayedB2CPrice
						}
					/>
					<section
						id="planos"
						className="bg-white px-6 py-20 md:px-10 md:py-24"
					>
						<div className="mx-auto max-w-[880px] space-y-14">
							<div className="space-y-3 text-center">
								<h4 className="font-albert text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
									Planos
								</h4>
								<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
									Escolha quantas sessões{' '}
									<span className="text-brand-orange">
										cabem no seu mês.
									</span>
								</h2>
								<p className="font-albert mx-auto max-w-[600px] text-[17px] leading-[1.75] text-[#696969]">
									Três ritmos. Mesmo psicólogo, mesmo cuidado,
									preço que entra no orçamento.
								</p>
							</div>

							{isIndividualAudience ? (
								<div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3 md:gap-4">
									{INDIVIDUAL_STATIC_PLANS.map((plan) => {
										const isHighlighted = plan.highlighted;
										return (
											<div
												key={plan.id}
												className={cn(
													'relative flex h-full min-h-0 flex-col rounded-2xl border border-[#e4e4e0] bg-[#faf9f6] px-6 py-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(0,0,0,0.08)]',
													isHighlighted &&
														'z-10 scale-[1.02] border-brand-orange bg-[#0c0c0c] text-white shadow-[0_18px_48px_rgba(255,113,70,0.22)]',
												)}
											>
												{isHighlighted && (
													<div className="font-albert absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-orange px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
														Mais escolhido
													</div>
												)}

												<div className="flex min-h-0 flex-1 flex-col">
													<div>
														<h3
															className={cn(
																'font-libre mb-1.5 text-[20px] font-bold leading-tight',
																isHighlighted
																	? 'text-white'
																	: 'text-[#1c1c1c]',
															)}
														>
															{plan.name}
														</h3>
														<p
															className={cn(
																'font-albert mb-6 text-[13px] leading-[1.55]',
																isHighlighted
																	? 'text-white/60'
																	: 'text-[#696969]',
															)}
														>
															{plan.slogan}
														</p>
														{plan.pricePrefix && (
															<p
																className={cn(
																	'font-albert mb-1 text-[13px] font-medium',
																	isHighlighted
																		? 'text-white/70'
																		: 'text-[#696969]',
																)}
															>
																{
																	plan.pricePrefix
																}
															</p>
														)}
														<div
															className={cn(
																'font-libre mb-1 text-[38px] leading-none',
																isHighlighted
																	? 'text-brand-orange'
																	: 'text-[#1c1c1c]',
															)}
														>
															R${' '}
															{plan.priceDisplay}
														</div>
														<p
															className={cn(
																'font-albert mb-[22px] text-[13px]',
																isHighlighted
																	? 'text-white/55'
																	: 'text-[#696969]',
															)}
														>
															por mês
														</p>
													</div>

													<ul
														className={cn(
															'mt-2 min-h-0 flex-1 space-y-3 border-t pt-5',
															isHighlighted
																? 'border-white/15'
																: 'border-[#e4e4e0]',
														)}
													>
														{plan.benefits.map(
															(label) => (
																<li
																	key={label}
																	className="font-albert flex items-start gap-x-2 text-[13.5px] font-medium leading-[1.55]"
																>
																	<Check
																		size={
																			16
																		}
																		className="mt-0.5 shrink-0 text-brand-orange"
																	/>
																	<span
																		className={
																			isHighlighted
																				? 'text-white/85'
																				: 'text-[#1c1c1c]'
																		}
																	>
																		{label}
																	</span>
																</li>
															),
														)}
													</ul>
												</div>

												<Button
													className={cn(
														'font-albert mt-4 shrink-0 justify-center rounded-lg py-3.5 text-[14px] font-bold normal-case tracking-[0.02em] transition-all',
														isHighlighted
															? 'border-none bg-brand-orange text-white hover:bg-brand-orange/90 hover:shadow-[0_12px_28px_rgba(255,113,70,0.32)]'
															: 'border border-[#1c1c1c] bg-white text-[#1c1c1c] hover:bg-[#1c1c1c] hover:text-white',
													)}
													text="Começar agora"
													onClick={() =>
														navigate(
															'/cadastro',
															createClubesAuthRedirectState(),
														)
													}
												/>
											</div>
										);
									})}
								</div>
							) : isLoadingB2CClubs ? (
								<div className="flex justify-center py-20">
									<Loader2
										className="animate-spin text-brand-orange"
										size={40}
									/>
								</div>
							) : (
								<div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3 md:gap-4">
									{sortedB2CClubs.map((club, index, arr) => {
										const totalCards = Math.min(
											arr.length,
											MAX_CLUBS_IN_INDIVIDUAL_GRID,
										);
										const isHighlighted =
											club.render_type === 'HIGHLIGHTED';
										if (index >= totalCards) return null;

										return (
											<div
												key={`${club.id}-${index}`}
												className={cn(
													'relative flex h-full min-h-0 flex-col rounded-2xl border border-[#e4e4e0] bg-[#faf9f6] px-6 py-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(0,0,0,0.08)]',
													isHighlighted &&
														'z-10 scale-[1.02] border-brand-orange bg-[#0c0c0c] text-white shadow-[0_18px_48px_rgba(255,113,70,0.22)]',
												)}
											>
												{isHighlighted && (
													<div className="font-albert absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-orange px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
														Mais escolhido
													</div>
												)}

												<div className="flex min-h-0 flex-1 flex-col">
													<div>
														<h3
															className={cn(
																'font-libre mb-1.5 text-[20px] font-bold leading-tight',
																isHighlighted
																	? 'text-white'
																	: 'text-[#1c1c1c]',
															)}
														>
															{club.name}
														</h3>
														{club.slogan && (
															<p
																className={cn(
																	'font-albert mb-6 text-[13px] leading-[1.55]',
																	isHighlighted
																		? 'text-white/60'
																		: 'text-[#696969]',
																)}
															>
																{club.slogan}
															</p>
														)}
														<div
															className={cn(
																'font-libre mb-1 text-[38px] leading-none',
																isHighlighted
																	? 'text-brand-orange'
																	: 'text-[#1c1c1c]',
															)}
														>
															{club.currency ===
															'BRL'
																? 'R$'
																: club.currency}{' '}
															{parseFloat(
																club.price,
															).toLocaleString(
																'pt-BR',
																{
																	minimumFractionDigits: 2,
																},
															)}
														</div>
														<p
															className={cn(
																'font-albert mb-[22px] text-[13px]',
																isHighlighted
																	? 'text-white/55'
																	: 'text-[#696969]',
															)}
														>
															por mês
														</p>
													</div>

													{/*
                          <button
                            type="button"
                            onClick={() => setSelectedClub(club)}
                            className={cn(
                              'ml-auto inline-flex items-center gap-1.5 rounded-full border px-4 py-2 font-albert text-[10px] font-bold uppercase tracking-[0.14em] transition-colors',
                              isHighlighted
                                ? 'border-brand-orange/40 bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20'
                                : 'border-[#e4e4e0] bg-white text-[#696969] hover:bg-[#f5f5f2]',
                            )}
                          >
                            Entenda o plano
                            <Info size={12} />
                          </button>
                          */}

													<ul
														className={cn(
															'mt-2 min-h-0 flex-1 space-y-3 border-t pt-5',
															isHighlighted
																? 'border-white/15'
																: 'border-[#e4e4e0]',
														)}
													>
														{sortBenefitsByPriority(
															club.benefits || [],
														)
															.slice(
																0,
																MAX_BENEFITS_IN_CARD,
															)
															.map(
																(
																	benefit: any,
																) => (
																	<li
																		key={
																			benefit.id
																		}
																		className="font-albert flex items-start gap-x-2 text-[13.5px] font-medium leading-[1.55]"
																	>
																		<Check
																			size={
																				16
																			}
																			className={cn(
																				'mt-0.5 shrink-0',
																				isHighlighted
																					? 'text-brand-orange'
																					: 'text-brand-orange',
																			)}
																		/>
																		<span
																			className={
																				isHighlighted
																					? 'text-white/85'
																					: 'text-[#1c1c1c]'
																			}
																		>
																			{benefit.service_substitute_title ||
																				benefit
																					.service
																					?.service_name}
																		</span>
																	</li>
																),
															)}
														{(club.benefits
															?.length || 0) >
															MAX_BENEFITS_IN_CARD && (
															<li
																className={cn(
																	'font-albert text-xs font-bold uppercase tracking-wider',
																	isHighlighted
																		? 'text-white/60'
																		: 'text-[#696969]',
																)}
															>
																+
																{(club.benefits
																	?.length ||
																	0) -
																	MAX_BENEFITS_IN_CARD}{' '}
																benefícios
															</li>
														)}
													</ul>
												</div>

												<Button
													className={cn(
														'font-albert mt-4 shrink-0 justify-center rounded-lg py-3.5 text-[14px] font-bold normal-case tracking-[0.02em] transition-all',
														isHighlighted
															? 'border-none bg-brand-orange text-white hover:bg-brand-orange/90 hover:shadow-[0_12px_28px_rgba(255,113,70,0.32)]'
															: 'border border-[#1c1c1c] bg-white text-[#1c1c1c] hover:bg-[#1c1c1c] hover:text-white',
													)}
													text="Começar agora"
													onClick={() =>
														navigate(
															'/cadastro',
															createClubesAuthRedirectState(),
														)
													}
												/>
											</div>
										);
									})}
								</div>
							)}

							{/* <div className="mx-auto mt-8 max-w-[760px] rounded-[14px] border border-[#e4e4e0] bg-[#f5f5f2] px-7 py-7 text-center">
                <strong className="font-libre block text-[16px] italic text-[#1c1c1c]">
                  Comparado a sessao avulsa de R$ 219,90.
                </strong>
                <p className="font-albert mt-2 text-[14px] leading-[1.65] text-[#696969]">
                  Cada sessao no plano Mensal sai por R$ 59,97 - quase 73% mais
                  barato que pagar consulta por consulta. E voce pode mudar de
                  plano a qualquer momento.
                </p>
              </div> */}
						</div>
					</section>

					<section className="bg-[#faf9f6] px-6 py-20 md:px-10 md:py-24">
						<div className="mx-auto max-w-[880px] space-y-12">
							<div className="space-y-3 text-center">
								<h4 className="font-albert text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
									Como funciona
								</h4>
								<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
									Sua próxima sessão{' '}
									<span className="text-brand-orange">
										em 3 cliques.
									</span>
								</h2>
							</div>

							<div className="grid items-center gap-10 md:grid-cols-2 md:gap-14">
								<ol className="space-y-6">
									<li className="grid grid-cols-[40px_1fr] gap-4">
										<div className="font-albert flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-[15px] font-bold text-white">
											1
										</div>
										<div className="space-y-1.5">
											<h3 className="font-libre text-[19px] font-bold text-[#1c1c1c]">
												Escolha seu plano
											</h3>
											<p className="font-albert text-[14.5px] leading-[1.7] text-[#696969]">
												Mensal, quinzenal ou semanal.
												Pague no cartão e libere a
												agenda na hora.
											</p>
										</div>
									</li>

									<li className="grid grid-cols-[40px_1fr] gap-4">
										<div className="font-albert flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-[15px] font-bold text-white">
											2
										</div>
										<div className="space-y-1.5">
											<h3 className="font-libre text-[19px] font-bold text-[#1c1c1c]">
												Agende com o psicólogo certo
											</h3>
											<p className="font-albert text-[14.5px] leading-[1.7] text-[#696969]">
												Conte rapidamente o que você
												procura. A gente sugere os
												profissionais que combinam, e
												você escolhe horário e dia.
											</p>
										</div>
									</li>

									<li className="grid grid-cols-[40px_1fr] gap-4">
										<div className="font-albert flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-[15px] font-bold text-white">
											3
										</div>
										<div className="space-y-1.5">
											<h3 className="font-libre text-[19px] font-bold text-[#1c1c1c]">
												Faça a sessão pelo navegador
											</h3>
											<p className="font-albert text-[14.5px] leading-[1.7] text-[#696969]">
												Sem instalar app. Vídeo seguro e
												sigiloso no celular ou no
												computador.
											</p>
										</div>
									</li>
								</ol>

								<button
									type="button"
									onClick={() =>
										setLightboxMedia({
											src: 'https://i.ibb.co/kVTN7Jm4/agendar.gif',
											alt: 'Demonstração de agendamento em poucos cliques',
										})
									}
									aria-label="Ampliar demonstração de agendamento"
									className="group relative block w-full cursor-pointer overflow-hidden rounded-[14px] border border-[#e4e4e0] bg-white p-0 shadow-[0_18px_48px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
								>
									<img
										src="https://i.ibb.co/kVTN7Jm4/agendar.gif"
										alt="Demonstração de agendamento em poucos cliques"
										className="h-full w-full scale-[1.03] object-cover"
										loading="lazy"
									/>
									<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100 group-focus-visible:bg-black/40 group-focus-visible:opacity-100">
										<Maximize2
											className="h-10 w-10 text-white drop-shadow"
											strokeWidth={2.25}
											aria-hidden
										/>
									</div>
								</button>
							</div>
						</div>
					</section>

					<section className="bg-white px-6 py-16 text-center md:px-10 md:py-[72px]">
						<div className="mx-auto max-w-[720px] space-y-6">
							<h2 className="font-libre text-[28px] font-bold leading-[1.25] tracking-[-0.01em] text-[#1c1c1c] md:text-[32px]">
								Sem fidelidade.{' '}
								<span className="text-brand-orange">
									Sem letra miúda.
								</span>
							</h2>
							<p className="font-albert mx-auto max-w-[580px] text-[15px] leading-[1.7] text-[#696969]">
								Você pode cancelar ou trocar de plano quando
								quiser, direto pela plataforma. Todas as sessões
								são sigilosas e os seus dados são tratados com
								segurança e transparência.
							</p>

							<div className="flex flex-wrap justify-center gap-2 pt-1">
								<span className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]">
									Sem fidelidade
								</span>
								<span className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]">
									Sigilo total
								</span>
								<span className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]">
									LGPD
								</span>
								<span className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]">
									Psicólogos com CRP
								</span>
								<span className="font-albert rounded-full border border-[#e4e4e0] bg-[#f5f5f2] px-3.5 py-1.5 text-[12.5px] font-medium text-[#1c1c1c]">
									Cancele quando quiser
								</span>
							</div>
						</div>
					</section>

					<section className="relative overflow-hidden bg-brand-orange px-6 py-20 text-center md:px-10 md:py-24">
						<div className="pointer-events-none absolute left-1/2 top-[-150px] h-[500px] w-[600px] -translate-x-1/2 bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,transparent_65%)]" />
						<div className="relative z-10 mx-auto max-w-[600px] space-y-5 text-white">
							<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] md:text-[44px]">
								Sua próxima sessão começa hoje.
							</h2>
							<p className="font-albert mx-auto max-w-[480px] text-[16px] leading-[1.7] text-white/90">
								Escolha um plano, marque o horário e converse
								com um psicólogo ainda esta semana.
							</p>
							<div className="flex justify-center pt-2">
								<Button
									onClick={handleCreateAccount}
									className="
                    font-albert rounded-lg bg-white
                    px-11 py-4 text-[15px] font-bold normal-case
                    tracking-[0.02em] text-[#c6542e] transition-all
                    duration-200 hover:-translate-y-0.5
                    hover:bg-white/95
                    hover:shadow-[0_14px_36px_rgba(0,0,0,0.2)]
                  "
									text={
										isIndividualAudience ||
										cheapestDisplayedB2CPrice !== null
											? `Ver planos a partir de R$ ${(isIndividualAudience
													? INDIVIDUAL_CHEAPEST_PRICE
													: cheapestDisplayedB2CPrice!
											  ).toLocaleString('pt-BR', {
													minimumFractionDigits: 2,
											  })}`
											: 'Ver planos'
									}
								/>
							</div>
							<p className="font-albert text-[12.5px] text-white/65">
								Sem fidelidade · cancele quando quiser
							</p>
						</div>
					</section>

					<section
						id="central-ajuda"
						className="bg-[#faf9f6] px-6 py-20 md:px-10 md:py-24"
					>
						<div className="mx-auto max-w-[880px] space-y-12">
							<div className="space-y-3 text-center">
								<h4 className="font-albert text-[11px] font-bold uppercase tracking-[0.22em] text-[#8a8a8a]">
									Informações importantes sobre o uso da
									plataforma.
								</h4>
								<h2 className="font-libre text-[30px] font-bold leading-[1.12] tracking-[-0.015em] text-[#1c1c1c] md:text-[44px]">
									Ficou com alguma dúvida?
								</h2>
							</div>
							{questionsData && (
								<Accordion
									questions={questionsData}
									itemsPerPage={5}
									variant="landing"
									className="px-0"
								/>
							)}
						</div>
					</section>

					<ReclameAquiVerifiedSection />
				</main>

				<Footer />
				<ClubPlanDetailsModal
					club={selectedClub}
					onClose={() => setSelectedClub(null)}
				/>
				<MediaLightbox
					isOpen={lightboxMedia !== null}
					onClose={() => setLightboxMedia(null)}
					src={lightboxMedia?.src ?? ''}
					alt={lightboxMedia?.alt}
				/>
			</section>
		);
	}

	return (
		<section className="flex w-full flex-col items-center bg-white text-gray-900">
			<Helmet>
				<title>Plumaa - Saúde Mental</title>
				<meta
					name="description"
					content="Encontre psicólogos e psiquiatras qualificados. Agende sua consulta online de forma segura e comece a cuidar da sua saúde mental hoje."
				/>
			</Helmet>

			<Header />

			<main className="w-full">
				<HeroSection isPatientAudience={false} />

				{/* 1. SESSÃO: CUIDADO EM TODAS AS FRENTES */}
				<section className="bg-white px-8 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-20">
						<div className="space-y-3 text-center">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-orange">
								Ecossistema Plumaa
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-stone-900 md:text-5xl">
								Cuidado em todas as frentes
							</h2>
						</div>

						<div className="grid grid-cols-1 gap-16 md:grid-cols-3">
							<div className="flex flex-col items-start gap-7">
								<div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-brand-pink text-white">
									<User size={28} />
								</div>
								<div className="space-y-4">
									<h3 className="text-2xl font-bold tracking-tight text-stone-900">
										Psicólogos
									</h3>
									<p className="max-w-[280px] text-sm font-medium leading-relaxed text-stone-500">
										Sessões online com especialistas
										experientes, sem burocracia. Escolha
										consciente do terapeuta com perfis
										claros.
									</p>
								</div>
								<button
									onClick={handleScheduling}
									className="group flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-stone-950 transition-all hover:opacity-70"
								>
									Saiba mais
									<ArrowRight
										size={16}
										className="transition-transform group-hover:translate-x-1"
									/>
								</button>
							</div>

							<div className="flex flex-col items-start gap-7">
								<div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-brand-purple text-white">
									<Stethoscope size={28} />
								</div>
								<div className="space-y-4">
									<h3 className="text-2xl font-bold tracking-tight text-stone-900">
										Médico
									</h3>
									<p className="max-w-[280px] text-sm font-medium leading-relaxed text-stone-500">
										Consultas online focadas no
										acompanhamento integral. Orientação
										clara e processo simplificado.
									</p>
								</div>
								<button
									onClick={handleScheduling}
									className="group flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-stone-950 transition-all hover:opacity-70"
								>
									Saiba mais
									<ArrowRight
										size={16}
										className="transition-transform group-hover:translate-x-1"
									/>
								</button>
							</div>

							<div className="flex flex-col items-start gap-7">
								<div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-brand-orange text-white">
									<Building2 size={28} />
								</div>
								<div className="space-y-4">
									<h3 className="text-2xl font-bold tracking-tight text-stone-900">
										RH & Empresa
									</h3>
									<p className="max-w-[280px] text-sm font-medium leading-relaxed text-stone-500">
										Dados reais de engajamento e bem-estar.
										Atendimento como benefício estratégico e
										comunicação clara.
									</p>
								</div>
								<button
									onClick={() =>
										window.open(
											WHATSAPP_LINK_FORM,
											'_blank',
										)
									}
									className="group flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-stone-950 transition-all hover:opacity-70"
								>
									Saiba mais
									<ArrowRight
										size={16}
										className="transition-transform group-hover:translate-x-1"
									/>
								</button>
							</div>
						</div>
					</div>
				</section>

				{/* 2. SESSÃO: MOMENTOS */}
				<section className="bg-stone-950 px-8 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-16">
						<div className="space-y-4 text-center">
							<h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
								A Plumaa em todos os seus momentos.
							</h2>
							<p className="mx-auto max-w-2xl text-sm text-stone-400 md:text-base">
								Do trabalho ao descanso, nossa tecnologia e rede
								clínica estão com você.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-8 md:grid-cols-3">
							{[
								{
									title: 'Alta Performance',
									img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=800',
								},
								{
									title: 'Foco & Decisão',
									img: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800',
								},
								{
									title: 'Paz e Descanso',
									img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800',
								},
							].map((item, idx) => (
								<div
									key={idx}
									className="group relative aspect-square cursor-pointer overflow-hidden rounded-[2rem] duration-700 md:rounded-[3rem]"
								>
									<img
										src={item.img}
										alt={item.title}
										className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
									/>
									<div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/90 via-black/20 to-transparent p-8 md:p-12">
										<div className="space-y-3">
											<h3 className="text-xl font-bold tracking-tight text-white md:text-2xl">
												{item.title}
											</h3>
											<div className="h-1.5 w-0 max-w-[160px] rounded-full bg-stone-50 transition-all duration-500 group-hover:w-full" />
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* 3. SESSÃO: PROFISSIONAIS */}
				<section className="bg-stone-100/50 px-8 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl space-y-16">
						<div className="space-y-3 text-center">
							<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-stone-400">
								Atendimento feito por psicólogos com experiência
								clínica.
							</h4>
							<h2 className="text-3xl font-bold tracking-tight text-stone-900 md:text-5xl">
								Profissionais qualificados
							</h2>
						</div>

						{profData && !isLoadingProfessional && (
							<ProfessionalsCarousel
								professionals={
									profData?.data ??
									profData?.professionals ??
									[]
								}
							/>
						)}
					</div>
				</section>

				{/* 4. SESSÃO: PEÇA PLUMAA */}
				<section className="w-full bg-white px-8 py-24 md:px-16 lg:px-24">
					<div className="mx-auto max-w-7xl">
						<div className="relative flex flex-col items-center justify-between gap-16 overflow-hidden rounded-[3.5rem] bg-stone-950 p-8 md:p-20 lg:flex-row">
							<div className="z-10 flex-1 space-y-8 text-center text-white lg:text-left">
								<h2 className="text-5xl font-black leading-[1.1] tracking-tighter text-stone-200 md:text-6xl">
									Peça{' '}
									<span className="text-white underline">
										Plumaa
									</span>{' '}
									<br />e sorria pro seu RH.
								</h2>
								<p className="max-w-lg text-base leading-relaxed text-stone-400 md:text-lg">
									A plataforma de saúde mental que une RH e
									Colaborador com o mesmo objetivo: equilíbrio
									real.
								</p>
							</div>

							<div className="z-10 flex w-full flex-col gap-10 rounded-[3rem] bg-white p-10 md:p-14 lg:w-[500px]">
								<div className="space-y-8">
									<h3 className="text-2xl font-bold leading-none text-stone-900">
										O que você ganha:
									</h3>
									<ul className="space-y-5">
										{[
											'Sessões de terapia online com psicólogos',
											'Acompanhamento contínuo, além da consulta',
											'Suporte direto para momentos de necessidade',
											'Ferramentas para acompanhar humor e rotina',
											'Conteúdos guiados para o dia a dia',
										].map((item, i) => (
											<li
												key={i}
												className="flex items-start gap-4 font-semibold leading-snug text-stone-600"
											>
												<CheckCircle2
													className="mt-0.5 shrink-0 text-brand-orange"
													size={22}
												/>
												{item}
											</li>
										))}
									</ul>
								</div>
								<Button
									className="justify-center rounded-2xl border-none bg-brand-pink py-6 text-xs font-black uppercase tracking-[0.2em] text-white transition-all hover:bg-brand-pink/90"
									text="Começar agora"
									onClick={() =>
										window.open(
											WHATSAPP_LINK_FORM,
											'_blank',
										)
									}
								/>
							</div>
						</div>
					</div>
				</section>

				{/* 5. SESSÃO: PLANOS DINÂMICOS */}
				{(isLoadingB2B2CClubs || sortedB2B2CClubs.length > 0) && (
					<section
						id="planos"
						className="bg-stone-100/50 px-8 py-24 md:px-16 lg:px-24"
					>
						<div className="mx-auto max-w-7xl space-y-16">
							<div className="space-y-3 text-center">
								<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-orange">
									Nossos Planos
								</h4>
								<h2 className="text-3xl font-bold tracking-tight text-stone-900 md:text-5xl">
									A solução certa para o tamanho do seu
									desafio
								</h2>
								<p className="mx-auto max-w-2xl text-stone-500">
									Escolha o cuidado que melhor se adapta às
									suas necessidades.
								</p>
							</div>

							{isLoadingB2B2CClubs ? (
								<div className="flex justify-center py-20">
									<Loader2
										className="animate-spin text-brand-orange"
										size={40}
									/>
								</div>
							) : (
								<div className="flex flex-wrap items-stretch justify-center gap-6 xl:flex-nowrap">
									{sortedB2B2CClubs.map(
										(club, index, arr) => {
											const totalCards = arr.length;
											const isHighlighted =
												club.render_type ===
												'HIGHLIGHTED';
											const desktopWidthClass =
												totalCards >= 4
													? 'xl:w-[calc((100%-72px)/4)]'
													: totalCards === 3
													  ? 'xl:w-[calc((100%-48px)/3)]'
													  : totalCards === 2
													    ? 'xl:w-[calc((100%-24px)/2)]'
													    : 'xl:w-[420px]';

											return (
												<div
													key={`${club.id}-${index}`}
													className={cn(
														'relative flex w-full flex-col justify-between gap-y-6 rounded-[2rem] border border-stone-200 bg-white p-6 transition-all duration-300 md:w-[calc(50%-12px)] xl:max-w-none',
														desktopWidthClass,
														isHighlighted &&
															'z-10 scale-105 border-stone-800 bg-stone-950 text-white',
													)}
												>
													{isHighlighted && (
														<div className="absolute -top-4 right-8 rounded-full border border-stone-800 bg-stone-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white">
															Recomendado
														</div>
													)}

													<div className="space-y-3">
														<div>
															<h3
																className={cn(
																	'mb-1.5 text-xl font-bold leading-tight',
																	isHighlighted
																		? 'text-white'
																		: 'text-stone-900',
																)}
															>
																{club.name}
															</h3>
															{club.slogan && (
																<p
																	className={cn(
																		'mb-6 text-sm font-medium leading-relaxed',
																		isHighlighted
																			? 'text-stone-400'
																			: 'text-stone-500',
																	)}
																>
																	{
																		club.slogan
																	}
																</p>
															)}
															<p
																className={cn(
																	'mb-1 text-[10px] font-black uppercase tracking-widest',
																	isHighlighted
																		? 'text-brand-orange'
																		: 'text-brand-orange',
																)}
															>
																{club.currency ===
																'BRL'
																	? 'R$'
																	: club.currency}{' '}
																{parseFloat(
																	club.price,
																).toLocaleString(
																	'pt-BR',
																	{
																		minimumFractionDigits: 2,
																	},
																)}{' '}
															</p>
															<p
																className={cn(
																	'mb-[22px] text-sm',
																	isHighlighted
																		? 'text-stone-400'
																		: 'text-stone-500',
																)}
															>
																por mês
															</p>
														</div>

														{/*
                          <button
                            type="button"
                            onClick={() => setSelectedClub(club)}
                            className={cn(
                              'ml-auto inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] transition-colors',
                              isHighlighted
                                ? 'border-brand-orange/40 bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20'
                                : 'border-stone-200 bg-white text-stone-500 hover:bg-stone-50',
                            )}
                          >
                            Entenda o plano
                            <Info size={12} />
                          </button>
                          */}

														<ul className="space-y-4">
															{sortBenefitsByPriority(
																club.benefits ||
																	[],
															)
																.slice(
																	0,
																	MAX_BENEFITS_IN_CARD,
																)
																.map(
																	(
																		benefit: any,
																	) => (
																		<li
																			key={
																				benefit.id
																			}
																			className="flex items-start gap-x-2 text-sm font-medium"
																		>
																			<Check
																				size={
																					16
																				}
																				className={cn(
																					'mt-0.5 shrink-0',
																					isHighlighted
																						? 'text-brand-orange'
																						: 'text-brand-orange',
																				)}
																			/>
																			<span
																				className={
																					isHighlighted
																						? 'text-stone-400'
																						: 'text-stone-500'
																				}
																			>
																				{benefit.service_substitute_title ||
																					benefit
																						.service
																						?.service_name}
																			</span>
																		</li>
																	),
																)}
															{(club.benefits
																?.length || 0) >
																MAX_BENEFITS_IN_CARD && (
																<li
																	className={cn(
																		'text-xs font-black uppercase tracking-wider',
																		isHighlighted
																			? 'text-stone-400'
																			: 'text-stone-500',
																	)}
																>
																	+
																	{(club
																		.benefits
																		?.length ||
																		0) -
																		MAX_BENEFITS_IN_CARD}{' '}
																	benefícios
																</li>
															)}
														</ul>
													</div>

													<Button
														className={cn(
															'mt-4 justify-center rounded-2xl border-none py-4 text-xs font-black uppercase tracking-[0.2em] transition-all',
															isHighlighted
																? 'bg-brand-orange text-stone-950 hover:bg-brand-orange/90'
																: 'bg-stone-950 text-white hover:bg-stone-800',
														)}
														text="Começar agora"
														onClick={() =>
															navigate(
																'/cadastro',
																createClubesAuthRedirectState(),
															)
														}
													/>
												</div>
											);
										},
									)}
								</div>
							)}
						</div>
					</section>
				)}

				{/* 6. CENTRAL DE AJUDA */}
				<section
					id="central-ajuda"
					className="bg-white px-8 py-24 md:px-16 lg:px-24"
				>
					<div className="mx-auto max-w-7xl space-y-16">
						<h2 className="text-center text-3xl font-bold tracking-tight text-stone-900 md:text-5xl">
							Ficou com alguma dúvida?
						</h2>
						{questionsData && (
							<Accordion
								questions={questionsData}
								itemsPerPage={5}
							/>
						)}
					</div>
				</section>

				<ReclameAquiVerifiedSection />
			</main>

			<Footer />
			<ClubPlanDetailsModal
				club={selectedClub}
				onClose={() => setSelectedClub(null)}
			/>
		</section>
	);
};

export { Landing };
